/**
 * Product page and quick-add behaviour (markup: snippets/pg-product-buy.liquid,
 * sections/pg-main-product.liquid, blocks/pg-product-share.liquid).
 *
 * - <pg-product-form>: quantity stepper, add to cart through @pg/cart (button busy → done),
 *   and button state for the variant chosen in @pg/variants (`pg:variant:change`). The form
 *   is read with FormData so app fields (EZ Pre-order properties, selling plans) travel along.
 *   Inside a dialog (quick add) the dialog closes after a successful add.
 * - <pg-sticky-buy>: the phone add-to-cart bar, shown once the main buttons scroll out of view.
 * - <pg-share>: Web Share API, with copy-link as the fallback.
 */
import { PgElement, define, t, toast } from '@pg/core';
import { addItems } from '@pg/cart';
import { closeDialog } from '@pg/ui';

class PgProductForm extends PgElement {
  mount() {
    this.form = /** @type {HTMLFormElement|null} */ (this.querySelector('form'));
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal: this.signal });
    this.addEventListener('click', (event) => this.#onStep(event), { signal: this.signal });
    this.querySelector('[data-pg-qty-input]')?.addEventListener('change', () => this.#syncSteppers(), { signal: this.signal });
    const root = this.closest('[data-pg-product]') || document;
    root.addEventListener('pg:variant:change', (event) => this.#onVariant(/** @type {CustomEvent} */ (event)), {
      signal: this.signal,
    });
  }

  /** Add buttons, including ones outside the form tied to it with form="…" (sticky bar). */
  get buttons() {
    if (!this.form) return [];
    return /** @type {HTMLButtonElement[]} */ ([...this.form.elements].filter((el) => el.matches('[data-pg-add]')));
  }

  /** @param {MouseEvent} event */
  #onStep(event) {
    const step = /** @type {Element} */ (event.target).closest('[data-pg-step]');
    if (!step) return;
    const input = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-pg-qty-input]'));
    if (!input) return;
    const increment = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(input.value) || min) + Number(step.getAttribute('data-pg-step')) * increment));
    input.value = String(next);
    this.#syncSteppers();
  }

  #syncSteppers() {
    const input = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-pg-qty-input]'));
    if (!input) return;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    if (!(Number(input.value) >= min)) input.value = String(min);
    const minus = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-pg-step="-1"]'));
    const plus = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-pg-step="1"]'));
    if (minus) minus.disabled = Number(input.value) <= min;
    if (plus) plus.disabled = Number(input.value) >= max;
  }

  /** @param {CustomEvent} event */
  #onVariant(event) {
    const variant = event.detail?.variant;
    const key = !variant ? 'labelUnavailable' : variant.available ? 'labelAdd' : 'labelSoldOut';
    const label = this.dataset[key] || '';
    for (const button of this.buttons) {
      button.disabled = !variant?.available;
      button.classList.remove('is-done');
      const wrapper = button.querySelector('.pg-button__label');
      if (wrapper) {
        wrapper.setAttribute('data-label', label);
        const span = wrapper.querySelector('span');
        if (span) span.textContent = label;
      }
    }
  }

  /** @param {SubmitEvent} event */
  async #onSubmit(event) {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (this.form);
    const buttons = this.buttons;
    if (buttons.some((b) => b.getAttribute('aria-busy') === 'true')) return;

    const data = new FormData(form);
    const id = Number(data.get('id'));
    if (!id) return;

    /** @type {{ id: number, quantity: number, selling_plan?: number, properties?: Record<string, string> }} */
    const item = { id, quantity: Math.max(1, Number(data.get('quantity')) || 1) };
    const sellingPlan = data.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    /** @type {Record<string, string>} */
    const properties = {};
    for (const [name, value] of data) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;

    const dialog = this.closest('dialog');
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      await addItems([item], { context: dialog ? 'dialog' : 'product', source: this });
      buttons.forEach((b) => {
        b.removeAttribute('aria-busy');
        b.classList.add('is-done');
      });
      setTimeout(() => buttons.forEach((b) => b.classList.remove('is-done')), 1600);
      if (dialog) setTimeout(() => closeDialog(dialog), 450);
    } catch {
      /* @pg/cart already showed the error toast */
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }
}

/** Phone add-to-cart bar: slides in once the main add button has scrolled above the viewport. */
class PgStickyBuy extends PgElement {
  mount() {
    const anchor = document.getElementById(this.dataset.anchor || '');
    if (!anchor || !('IntersectionObserver' in window)) return;
    this.#setVisible(false);
    new IntersectionObserver(([entry]) => {
      // Only after scrolling past it, not while it's still below the fold on load.
      this.#setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(anchor);
  }

  #setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);
    document.documentElement.classList.toggle('pg-has-sticky-buy', visible);
  }

  unmount() {
    document.documentElement.classList.remove('pg-has-sticky-buy');
  }
}

class PgShare extends PgElement {
  mount() {
    this.refs.button?.addEventListener('click', () => this.#share(), { signal: this.signal });
  }

  async #share() {
    const url = this.dataset.url || location.href;
    const title = this.dataset.title || document.title;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if (/** @type {Error} */ (error).name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast(t('copied'), { icon: 'check-circle' });
      this.refs.button?.classList.add('is-done');
      setTimeout(() => this.refs.button?.classList.remove('is-done'), 1600);
    } catch {
      window.prompt(title, url);
    }
  }
}

define('pg-product-form', PgProductForm);
define('pg-sticky-buy', PgStickyBuy);
define('pg-share', PgShare);
